import { useMutation } from "convex/react"
import { useQuery } from "convex/react"
import { api } from "../../../../convex/_generated/api"

/** Draft keyed by profile field names; the hook owns the companies-table translation. */
export type CompanyProfileDraft = {
  companyName?: string
  companyWebsite?: string
  companyLocation?: string
  companyOneLiner?: string
  companyAbout?: string
  companySize?: string
  companyStage?: string
  companyLinkedin?: string
  companyTwitter?: string
  companyInstagram?: string
}

/**
 * Full-form save args. Empty select fields are omitted so a blank control
 * cannot unset location/size/stage. Empty text/url fields stay present so
 * the user can clear them.
 */
export function toCompanyUpdateArgs(draft: CompanyProfileDraft) {
  const name = draft.companyName?.trim()
  const omitBlank = (value: string | undefined) => {
    const trimmed = value?.trim()
    return trimmed ? trimmed : undefined
  }
  return {
    name: name || undefined,
    website: draft.companyWebsite,
    location: omitBlank(draft.companyLocation),
    oneLiner: draft.companyOneLiner,
    about: draft.companyAbout,
    size: omitBlank(draft.companySize),
    stage: omitBlank(draft.companyStage),
    linkedin: draft.companyLinkedin,
    twitter: draft.companyTwitter,
    instagram: draft.companyInstagram,
  }
}

/** The canonical server command owns the bounded profile projection. */
function useSaveCompanyProfile() {
  const updateCompany = useMutation(api.companies.updateMyCompany)

  return async (draft: CompanyProfileDraft) => {
    await updateCompany(toCompanyUpdateArgs(draft))
  }
}

export function useCompanySettingsData() {
  const company = useQuery(api.companies.myCompany)
  const saveCompanyProfile = useSaveCompanyProfile()
  const setLogo = useMutation(api.companies.setLogo)

  return {
    company,
    saveCompanyProfile,
    setLogo,
  }
}
